import {
  Button,
  FormControl,
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CaretLeft,
  CaretRight,
  Gear,
  Plus,
  Trash,
} from '@phosphor-icons/react';
import { useState } from 'react';
import { useFormBuilder } from '../../Context/FormBuilderContext';
import { ActionTypes } from '../../Reducer';
import { canAddPage, getNextPageNumber } from '../../utils/pageUtils';

export function PageNavigation() {
  const {
    state,
    dispatch,
    currentPageId,
    setCurrentPageId,
    setSettingsFormMode,
  } = useFormBuilder();
  const pages = state.form.pages || [];
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const openPopover = Boolean(anchorEl);
  const currentPageOrientation =
    state.form.formStyle?.pageOrientation || 'SWAP_PAGE';

  if (pages.length === 0) {
    return null;
  }

  const currentPageIndex = pages.findIndex(page => page.id === currentPageId);
  const canGoPrevious = currentPageIndex > 0;
  const canGoNext = currentPageIndex < pages.length - 1;
  const canAdd = canAddPage(pages, 2);

  const handlePrevious = () => {
    if (canGoPrevious) {
      const previousPage = pages[currentPageIndex - 1];
      setCurrentPageId(previousPage.id);

      setSettingsFormMode(true);
    }
  };

  const handleNext = () => {
    if (canGoNext) {
      const nextPage = pages[currentPageIndex + 1];
      setCurrentPageId(nextPage.id);

      setSettingsFormMode(true);
    }
  };

  const handlePageClick = (pageId: string) => {
    setCurrentPageId(pageId);

    setSettingsFormMode(true);
  };

  const handleAddPage = () => {
    if (canAdd) {
      const uuid = crypto.randomUUID();

      const pageNumber = getNextPageNumber(pages);
      const newPageId = uuid;

      dispatch({
        type: ActionTypes.ADD_PAGE,
        payload: { name: `Page ${pageNumber}`, pageId: newPageId },
      });

      setCurrentPageId(newPageId);
      setSettingsFormMode(true);
    }
  };

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleOrientationChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newOrientation = event.target.value;
    dispatch({
      type: ActionTypes.UPDATE_FORM_STYLES,
      payload: {
        ...state.form.formStyle,
        pageOrientation: newOrientation,
      },
    });
  };

  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        borderTop: '1px solid #EAECF0',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
      }}
    >
      <Button
        variant="outlined"
        size="small"
        sx={{
          width: '114px',
          minWidth: '114px',
          maxWidth: '114px',
          borderRadius: '8px',
          color: '#667085',
          fontSize: '16px',
        }}
        startIcon={<CaretLeft size={16} />}
        onClick={handlePrevious}
        disabled={!canGoPrevious}
      >
        Previous
      </Button>

      <div
        style={{
          display: 'flex',
          gap: '20px',
          flex: 1,
          justifyContent: 'center',
          overflowX: 'auto',
          alignItems: 'center',
        }}
      >
        {pages.map(page => (
          <div key={page.id} style={{ display: 'flex', gap: '8px' }}>
            <Button
              variant={page.id === currentPageId ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handlePageClick(page.id)}
              style={{
                padding: '8px 10px',
                borderRadius: '8px',
              }}
            >
              {page.name}
            </Button>

            {pages.length > 1 && (
              <Tooltip
                title={
                  <Typography
                    sx={{
                      fontSize: '12px',
                      whiteSpace: 'pre-line',
                      textAlign: 'center',
                    }}
                  >
                    Delete page
                  </Typography>
                }
                arrow
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    if (
                      window.confirm(
                        `Remove page "${page.name}"? All nodes in this page will be deleted.`,
                      )
                    ) {
                      dispatch({
                        type: ActionTypes.REMOVE_PAGE,
                        payload: { pageId: page.id },
                      });

                      if (page.id === currentPageId) {
                        const remainingPages = (state.form.pages || []).filter(
                          p => p.id !== page.id,
                        );
                        if (remainingPages.length > 0) {
                          setCurrentPageId(remainingPages[0].id);
                        } else {
                          setCurrentPageId(null);
                        }
                      }
                    }
                  }}
                  sx={{
                    padding: '4px 4px',
                    border: 'none',
                    width: '32px',
                    minWidth: '32px',
                    maxWidth: '32px',
                    height: '32px',
                    color: '#667085',
                    '&:hover': {
                      backgroundColor: '#FFEBEE',
                      border: 'none',
                      color: '#F44336',
                    },
                  }}
                >
                  <Trash size={16} />
                </Button>
              </Tooltip>
            )}
          </div>
        ))}

        <div
          style={{ width: '1px', height: '24px', backgroundColor: '#EAECF0' }}
        />

        <div style={{ display: 'flex', gap: '8px' }}>
          {canAdd && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleAddPage}
              sx={{
                padding: '4px 4px',
                borderRadius: '8px',
                width: '32px',
                minWidth: '32px',
                maxWidth: '32px',
                height: '32px',
              }}
            >
              <Plus size={16} color="#475467" />
            </Button>
          )}

          <Tooltip
            title={
              <Typography
                sx={{
                  fontSize: '12px',
                  whiteSpace: 'pre-line',
                  textAlign: 'center',
                }}
              >
                Choose how you want the{'\n'}final navigation to look like
              </Typography>
            }
            arrow
          >
            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenPopover}
              sx={{
                padding: '4px 4px',
                borderRadius: '8px',
                width: '32px',
                minWidth: '32px',
                maxWidth: '32px',
                height: '32px',
              }}
            >
              <Gear size={16} color="#475467" />
            </Button>
          </Tooltip>
        </div>
      </div>

      <Button
        variant="outlined"
        size="small"
        sx={{
          width: '114px',
          minWidth: '114px',
          maxWidth: '114px',
          borderRadius: '8px',
          color: '#667085',
          fontSize: '16px',
        }}
        endIcon={<CaretRight size={16} />}
        onClick={handleNext}
        disabled={!canGoNext}
      >
        Next
      </Button>

      <Popover
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            marginTop: '8px',
            minWidth: '120px',
            minHeight: '145px',
            padding: '16px',
          },
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: '500',
            color: '#101828',
            marginBottom: '10px',
          }}
        >
          Final UI
        </Typography>

        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={currentPageOrientation}
            onChange={handleOrientationChange}
          >
            <FormControlLabel
              value="SWAP_PAGE"
              control={<Radio size="small" />}
              label={
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#475467',
                  }}
                >
                  Swap Page
                </Typography>
              }
              sx={{
                '& .MuiRadio-root': {
                  color: '#D0D5DD',
                  '&.Mui-checked': {
                    color: '#4F46E5',
                  },
                },
              }}
            />
            <FormControlLabel
              value="SIDE_BY_SIDE"
              control={<Radio size="small" />}
              label={
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#475467',
                  }}
                >
                  Side by side
                </Typography>
              }
              sx={{
                '& .MuiRadio-root': {
                  color: '#D0D5DD',
                  '&.Mui-checked': {
                    color: '#4F46E5',
                  },
                },
              }}
            />
            <FormControlLabel
              value="PEEKING"
              control={<Radio size="small" />}
              label={
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#475467',
                  }}
                >
                  Peeking
                </Typography>
              }
              sx={{
                '& .MuiRadio-root': {
                  color: '#D0D5DD',
                  '&.Mui-checked': {
                    color: '#4F46E5',
                  },
                },
              }}
            />
          </RadioGroup>
        </FormControl>
      </Popover>
    </div>
  );
}
